import type { EquippedCosmetics, UnlockKind } from '@anidex/shared/economy';
import type { Page, UnlockDTO } from '@anidex/shared/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unlockKindLabel, unlockLabel } from '../i18n/catalog';
import { useUnlockGroups, useUnlockKind } from '../services/queries';
import { CosmeticPreview } from './cosmetics/CosmeticPreview';
import { ErrorState, Spinner } from './States';

const GROUPS: UnlockKind[] = ['title', 'badge', 'frame', 'avatar', 'banner'];


export function UnlockMark({
  kind,
  label,
  unlockKey,
  size = 'inline',
  equipped = false,
}: {
  kind: UnlockKind;
  label: string;
  unlockKey?: string;
  size?: 'inline' | 'showcase';
  equipped?: boolean;
}) {
  const { t } = useTranslation();
  const shown = unlockKey ? unlockLabel(kind, unlockKey, label) : label;
  const showcase = size === 'showcase';
  return (
    <span className={`unlock-mark unlock-mark--${kind}${showcase ? ' unlock-mark--showcase' : ''}${equipped ? ' is-equipped' : ''}`}>
      <CosmeticPreview kind={kind} itemKey={unlockKey ?? ''} label={shown} compact={!showcase} />
      {kind !== 'title' ? <span className="unlock-mark__label">{shown}</span> : null}
      <span className="visually-hidden">
        , {unlockKindLabel(kind)}
        {equipped ? t('unlocks.equippedSuffix') : ''}
      </span>
    </span>
  );
}

function isWorn(item: UnlockDTO, worn: EquippedCosmetics | null): boolean {
  if (!worn) return false;
  if (item.kind === 'badge') return worn.badges.includes(item.key);
  return worn[item.kind] === item.key;
}

export function UnlockReveal({
  owner,
  worn = null,
  emptyText,
}: {
  
  owner: string | null;
  worn?: EquippedCosmetics | null;
  emptyText: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="button button--quiet" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {open ? t('unlocks.hide') : t('unlocks.show')}
      </button>
      {open ? <UnlockGallery owner={owner} worn={worn} emptyText={emptyText} /> : null}
    </>
  );
}

function UnlockGallery({ owner, worn, emptyText }: { owner: string | null; worn: EquippedCosmetics | null; emptyText: string }) {
  const groups = useUnlockGroups(owner, true);
  if (groups.isPending) return <Spinner />;
  if (groups.isError) return <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />;
  const filled = GROUPS.filter((kind) => groups.data[kind].items.length > 0);
  if (filled.length === 0) return <p className="hint">{emptyText}</p>;

  return (
    <div className="unlock-gallery">
      {filled.map((kind) => (
        <UnlockKindGroup key={kind} owner={owner} kind={kind} firstPage={groups.data[kind]} worn={worn} />
      ))}
    </div>
  );
}

function UnlockKindGroup({
  owner,
  kind,
  firstPage,
  worn,
}: {
  owner: string | null;
  kind: UnlockKind;
  firstPage: Page<UnlockDTO>;
  worn: EquippedCosmetics | null;
}) {
  const { t } = useTranslation();
  const pages = useUnlockKind(owner, kind, firstPage);
  const items = pages.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <section className="unlock-gallery__group" aria-labelledby={`unlock-group-${kind}`}>
      <h3 id={`unlock-group-${kind}`}>{t(`unlocks.groups.${kind}`)}</h3>
      <ul className={`unlock-tray unlock-tray--${kind}`}>
        {items.map((item) => (
          <li key={`${item.kind}-${item.key}`}>
            <UnlockMark kind={item.kind} unlockKey={item.key} label={item.label} size="showcase" equipped={isWorn(item, worn)} />
          </li>
        ))}
      </ul>
      {pages.hasNextPage ? (
        <button
          type="button"
          className="button button--small"
          onClick={() => void pages.fetchNextPage()}
          disabled={pages.isFetchingNextPage}
        >
          {t('unlocks.more')}
        </button>
      ) : null}
    </section>
  );
}
